import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { usersApi, type DeletedUser } from '@/api/users';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Avatar } from '@/components/ui/Avatar';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { humanizeError } from '@/lib/errors';
import { lifecycleErrorText, restoreSummary } from '@/lib/enrollmentActions';

const PAGE = 50;

type RowError = { text: string; conflictId?: string };

/**
 * Корзина удалённых учёток (users этап 4, договор users-stage4 §3–§4).
 *
 * Только admin: маршрут под `RequireAdmin`, пункт меню скрыт у manager, сервер
 * отвечает 403 всем остальным.
 *
 * ⚠️ ЗАНЯТАЯ ПОЧТА. Восстановление при `email_conflict` сервер откажет с
 * `email_taken` и НИЧЕГО не изменит (F2). Кнопку не прячем — конфликт мог
 * разрешиться, пока список висел открытым, — но предупреждаем рядом и ведём на
 * ту учётку. Слияния учёток здесь нет: это этап 1.
 */
export function AdminTrashPage() {
  const { t, i18n } = useTranslation();
  const ask = useConfirm();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, RowError>>({});

  const list = useQuery({
    queryKey: ['admin', 'trash', q, offset],
    queryFn: () => usersApi.listDeleted({ q: q || undefined, limit: PAGE, offset }),
  });

  const ROLE_LABELS: Record<string, string> = {
    student: t('adminDetail.roleStudent'),
    curator: t('adminDetail.roleCurator'),
    manager: t('adminDetail.roleManager'),
    admin: t('adminDetail.roleAdmin'),
  };

  const locale = i18n.language === 'kk' ? 'kk-KZ' : i18n.language === 'en' ? 'en-GB' : 'ru-RU';
  const when = (iso: string) => new Date(iso).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });

  const restore = useMutation({
    mutationFn: (u: DeletedUser) => usersApi.restore(u.id),
    onSuccess: (res, u) => {
      const s = restoreSummary(res);
      const parts = [t('adminTrash.doneRestore', { name: u.full_name || u.email, count: s.restored })];
      if (s.skipped > 0) parts.push(t('adminTrash.doneSkipped', { count: s.skipped }));
      if (s.primaryDropped > 0) parts.push(t('adminTrash.donePrimaryDropped', { count: s.primaryDropped }));
      setNotice(parts.join(' '));
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[u.id];
        return next;
      });
      void qc.invalidateQueries({ queryKey: ['admin', 'trash'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'students'] });
    },
    onError: (e, u) => {
      // Отказ показываем у самой строки: строка остаётся, сервер ничего не менял.
      const raw = (e as { raw?: { conflict_user_id?: unknown } } | null)?.raw;
      const conflictId = typeof raw?.conflict_user_id === 'string' ? raw.conflict_user_id : undefined;
      setRowErrors((prev) => ({ ...prev, [u.id]: { text: lifecycleErrorText(e, t), conflictId } }));
    },
  });

  const onRestore = async (u: DeletedUser) => {
    setNotice(null);
    if (
      await ask({
        message: t('adminTrash.confirmRestore', { name: u.full_name || u.email }),
        confirmLabel: t('adminTrash.restore'),
      })
    ) {
      restore.mutate(u);
    }
  };

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  return (
    <>
      <PageHeader
        title={t('adminTrash.title')}
        subtitle={list.data ? t('adminTrash.subtitle', { count: total }) : t('common.loading')}
      />
      <p className="mb-4 text-sm text-on-surface-variant">{t('adminTrash.hint')}</p>

      <form
        data-trash-search
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQ(draft.trim());
          setOffset(0);
        }}
      >
        <div className="min-w-0 flex-1">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('adminTrash.searchPlaceholder')}
            leftIcon={<span className="material-symbols-outlined">search</span>}
          />
        </div>
        <Button type="submit" variant="secondary">
          {t('adminTrash.search')}
        </Button>
      </form>

      {notice && (
        <div data-trash-notice role="status">
          <Card variant="subtle" className="mb-4 text-sm text-on-surface">
            {notice}
          </Card>
        </div>
      )}

      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : list.isError ? (
        <Card variant="subtle" className="text-error">
          {humanizeError(list.error)}
        </Card>
      ) : items.length === 0 ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('adminTrash.empty')}
        </Card>
      ) : (
        <ul className="space-y-2">
          {items.map((u) => {
            const err = rowErrors[u.id];
            return (
              <li key={u.id} data-trash-row={u.id}>
                <Card variant="subtle" className="flex flex-col gap-3 md:flex-row md:items-start">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <Avatar name={u.full_name} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="break-words font-semibold text-on-surface">
                        {u.full_name || t('adminMarathonDetail.noName')}
                      </p>
                      <p className="truncate text-xs text-on-surface-variant">{u.email}</p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        <Pill variant="neutral">{ROLE_LABELS[u.role] ?? u.role}</Pill>
                        {u.legacy && <Pill variant="warning">{t('adminTrash.legacyBadge')}</Pill>}
                        {u.email_conflict && <Pill variant="danger">{t('adminTrash.conflictBadge')}</Pill>}
                      </div>

                      {u.legacy ? (
                        <p className="mt-2 text-[13px] text-on-surface-variant">{t('adminTrash.legacy')}</p>
                      ) : (
                        <div className="mt-2 space-y-0.5 text-[13px] text-on-surface-variant">
                          <p>
                            {u.source === 'self'
                              ? t('adminTrash.removedSelf')
                              : t('adminTrash.removedBy', { name: u.removed_by?.name || '—' })}
                          </p>
                          {u.removed_at && <p>{t('adminTrash.removedAt', { date: when(u.removed_at) })}</p>}
                          <p className="break-words">
                            {u.reason ? t('adminTrash.reason', { reason: u.reason }) : t('adminTrash.noReason')}
                          </p>
                          <p>{t('adminTrash.enrollments', { count: u.enrollments_deactivated ?? 0 })}</p>
                        </div>
                      )}

                      {u.email_conflict && (
                        <p className="mt-2 text-[13px] text-error">
                          {t('adminTrash.conflict')}{' '}
                          {u.conflict_user_id && (
                            <Link to={`/admin/students/${u.conflict_user_id}`} className="font-semibold underline">
                              {t('adminTrash.conflictOpen')}
                            </Link>
                          )}
                        </p>
                      )}
                      {err && (
                        <p className="mt-2 text-[13px] text-error" role="alert">
                          {err.text}{' '}
                          {err.conflictId && (
                            <Link to={`/admin/students/${err.conflictId}`} className="font-semibold underline">
                              {t('adminTrash.conflictOpen')}
                            </Link>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    data-restore
                    className="w-full md:w-auto"
                    loading={restore.isPending && restore.variables?.id === u.id}
                    onClick={() => void onRestore(u)}
                  >
                    <span className="material-symbols-outlined">restore</span>
                    {t('adminTrash.restore')}
                  </Button>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {total > PAGE && (
        <div className="mt-4 flex items-center justify-between gap-2 text-sm text-on-surface-variant">
          <Button variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            {t('adminTrash.prev')}
          </Button>
          <span>
            {t('adminTrash.range', { from: offset + 1, to: Math.min(offset + PAGE, total), total })}
          </span>
          <Button variant="ghost" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
            {t('adminTrash.next')}
          </Button>
        </div>
      )}
    </>
  );
}
